import { LinkProps } from '@mui/material';
import { createTheme } from '@mui/material/styles';

import { LinkBehavior } from '@barboxx/admin-components';
import { CustomColorPallet } from '@barboxx/utils';

import type {} from '@mui/x-data-grid/themeAugmentation';

import { grey } from '@mui/material/colors';

const fontFamilies = [
    // 'Avenir Next',
    'Inter',
    "'-apple - system'",
    'BlinkMacSystemFont',
    'Segoe UI',
    'Helvetica',
    'Arial',
    "'sans - serif'",
    'Apple Color Emoji',
    'Segoe UI Emoji',
];

export default createTheme({
    cssVariables: {
        colorSchemeSelector: 'data-toolpad-color-scheme',
    },
    defaultColorScheme: 'dark',
    colorSchemes: {
        light: {
            palette: {
                mode: 'light',
                primary: {
                    main: CustomColorPallet.coreColors.primary,
                },
                secondary: {
                    main: CustomColorPallet.coreColors.secondary,
                },
                background: {
                    default: CustomColorPallet.schemes.light.background,
                    paper: grey[100],
                },
                // DataGrid: {
                //     bg: grey[50],
                //     pinnedBg: grey[100],
                //     headerBg: grey[200],
                // },
            },
        },
        dark: {
            palette: {
                mode: 'dark',
                primary: {
                    main: CustomColorPallet.coreColors.primary,
                },
                secondary: {
                    main: CustomColorPallet.coreColors.secondary,
                },
                background: {
                    default: CustomColorPallet.schemes.dark.background,
                    paper: grey[900],
                },
                // DataGrid: {
                // bg: grey[900],
                //     pinnedBg: grey[800],
                //     headerBg: grey[700],
                // },
                // AppBar: {
                //     darkBg: grey[300],
                // },
            },
        },
    },

    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 1000,
            lg: 1200,
            xl: 1920,
            '2xl': 2400,
        },
    },
    components: {
        // MuiDrawer: {
        //     defaultProps: {
        //         slotProps: {
        //             paper: {
        //                 sx: {
        //                     backgroundColor: 'grey.900',
        //                 },
        //             },
        //         },
        //     },
        // },
        // MuiListSubheader: {
        //     styleOverrides: {
        //         root: ({ theme }) =>
        //             theme.applyStyles('dark', {
        //                 backgroundColor: grey[800],
        //             }),
        //     },
        // },
        // MuiAppBar: {
        //     defaultProps: {
        //         position: 'fixed',
        //     },
        //     styleOverrides: {
        //         root: ({ theme }) => ({
        //             boxShadow: theme.shadows[3],
        //             backgroundColor: grey[900],
        //         }),
        //     },
        // },
        MuiLink: {
            defaultProps: {
                component: LinkBehavior,
            } as LinkProps,
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
                LinkComponent: LinkBehavior,
            },
        },
        MuiDataGrid: {
            defaultProps: {
                slotProps: {
                    loadingOverlay: {
                        variant: 'linear-progress',
                        noRowsVariant: 'skeleton',
                    },
                },
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: 'inherit',
                }),
                columnHeader: ({ theme }) => ({
                    // backgroundColor: theme.palette.grey[800],
                    // color: 'inherit',
                }),
            },
        },

        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
                sizeSmall: {
                    padding: '6px 16px',
                },
                sizeMedium: {
                    padding: '8px 20px',
                },
                sizeLarge: {
                    padding: '11px 24px',
                },
                textSizeSmall: {
                    padding: '7px 12px',
                },
                textSizeMedium: {
                    padding: '9px 16px',
                },
                textSizeLarge: {
                    padding: '12px 16px',
                },
            },
        },

        // MuiCard: {
        //     defaultProps: {
        //         variant: 'elevation',
        //         raised: true,
        //     },
        // },

        MuiCardContent: {
            styleOverrides: {
                root: ({ theme }) => ({
                    padding: `${theme.spacing(4)} ${theme.spacing(3)}`,
                    '&:last-child': {
                        paddingBottom: theme.spacing(4),
                    },
                }),
            },
        },
        MuiCardHeader: {
            defaultProps: {
                titleTypographyProps: {
                    variant: 'h6',
                },
                subheaderTypographyProps: {
                    variant: 'body2',
                },
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    padding: theme.spacing(3),
                    borderBottomColor: theme.palette.divider,
                    borderBottomWidth: '1px',
                    borderBottomStyle: 'solid',
                }),
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                '*': {
                    boxSizing: 'border-box',
                    margin: 0,
                    padding: 0,
                },
                html: {
                    MozOsxFontSmoothing: 'grayscale',
                    WebkitFontSmoothing: 'antialiased',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                },
                body: {
                    display: 'flex',
                    flex: '1 1 auto',
                    flexDirection: 'column',
                    minHeight: '100%',
                    height: '100%',
                    width: '100%',
                    fontFamily: fontFamilies.join(','),
                },
                '#root': {
                    display: 'flex',
                    flex: '1 1 auto',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    position: 'relative',
                },
            },
        },

        MuiInputBase: {
            styleOverrides: {
                input: ({ theme }) => ({
                    '& :-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 100px ${theme.palette.grey[900]} inset`,
                    },
                }),
            },
        },

        MuiTableHead: {
            styleOverrides: {
                root: {
                    backgroundColor: '#F3F4F6',
                    '.MuiTableCell-root': {
                        color: '#374151',
                    },
                    borderBottom: 'none',
                    '& .MuiTableCell-root': {
                        borderBottom: 'none',
                        fontSize: '12px',
                        fontWeight: 600,
                        lineHeight: 1,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                    },
                    '& .MuiTableCell-paddingCheckbox': {
                        paddingTop: 4,
                        paddingBottom: 4,
                    },
                },
            },
        },
        MuiAlert: {
            defaultProps: {
                variant: 'filled',
            },
        },
    },
    shape: {
        borderRadius: 8,
    },
    typography: {
        button: {
            fontWeight: 600,
        },
        fontFamily: fontFamilies.join(','),
        body1: {
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: 1.5,
        },
        body2: {
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: 1.57,
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 1.75,
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.57,
        },
        overline: {
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.5px',
            lineHeight: 2.5,
            textTransform: 'uppercase',
        },
        caption: {
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: 1.66,
        },
        h1: {
            fontWeight: 700,
            fontSize: '3.5rem',
            lineHeight: 1.375,
        },
        h2: {
            fontWeight: 700,
            fontSize: '3rem',
            lineHeight: 1.375,
        },
        h3: {
            fontWeight: 700,
            fontSize: '2.25rem',
            lineHeight: 1.375,
        },
        h4: {
            fontWeight: 700,
            fontSize: '2rem',
            lineHeight: 1.375,
        },
        h5: {
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.375,
        },
        h6: {
            fontWeight: 600,
            fontSize: '1.125rem',
            lineHeight: 1.375,
        },
    },
});
